// Writing a recipe (redesign slice 7b; wireframes 18 to 22;
// docs/2026-10-04-recipes-design.md): turning typed and pasted text into
// ingredients and steps, keeping a web-address source as the source link,
// and what the review screen flags. Pure functions, tested in
// tests/recipe-entry.test.mjs.
//
// Ingredient lines are split by the web importer's own parser and aisle
// lookup (supabase/functions/import-recipe-from-url/mapToSchema.ts), so a
// typed line and an imported one come out the same way.
import { categoryForIngredient, parseIngredientLine, splitAndList } from "../../supabase/functions/import-recipe-from-url/mapToSchema.ts";
import { NON_NUMERIC_UNITS } from "./constants";
import { uid } from "./utils";
import type { Recipe } from "./types";
import type { Ingredient, Instruction } from "./recipeDisplay";

export interface EntryIngredient extends Ingredient {
  id: string;
  component: string;
  type: "ingredient" | "note";
  category: string;
}

export interface EntryStep extends Instruction {
  id: string;
  component: string;
  type: "step" | "note";
  text: string;
}

// A line that names a section: "For the sauce:", "Topping:".
const SECTION_LINE = /^(?:for\s+(?:the\s+)?)?(.{1,60}?):\s*$/i;
// "Note: ..." / "Tip: ..."
const NOTE_LINE = /^(?:note|tip)s?\s*[:-]\s*(.+)$/i;
// Bullets and numbering people paste in: "- ", "• ", "▢ ", "1. ", "2) ", "Step 3:".
const LEADER = /^\s*(?:[-*•·▢□✓]+\s*|\(?\d{1,2}[.)]\s+|step\s+\d{1,2}\s*[:.-]?\s*)/i;
const STARTS_WITH_AMOUNT = /^\s*(?:\d|[¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])/;

const capitalize = (s: string) => s.replace(/^./, (c) => c.toUpperCase());

// One typed ingredient line, split into amount, unit, name and prep, with an
// aisle. `unclear` when the line had no amount to read (the parser then
// assumes 1) and isn't a "to taste" kind of line.
export function ingredientFromLine(line: string, component = ""): { ingredient: EntryIngredient; unclear: boolean } {
  const text = line.replace(LEADER, "").trim();
  const parsed = parseIngredientLine(text);
  const nonNumeric = NON_NUMERIC_UNITS.includes(parsed.unit);
  return {
    ingredient: { id: uid(), component, type: "ingredient", name: parsed.name, prep: parsed.prep, quantity: parsed.quantity, unit: parsed.unit, category: categoryForIngredient(parsed.name) },
    unclear: !nonNumeric && !STARTS_WITH_AMOUNT.test(text),
  };
}

// A whole pasted list: one ingredient per line ("salt and pepper" becomes
// two), section lines start a section, "Note:" lines become notes, blank
// lines are skipped. Returns the new rows and the ids of unclear ones.
export function ingredientsFromText(text: string, component = ""): { ingredients: EntryIngredient[]; unclear: string[] } {
  const ingredients: EntryIngredient[] = [];
  const unclear: string[] = [];
  let section = component;
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.replace(LEADER, "").trim();
    if (!line) continue;
    const note = NOTE_LINE.exec(line);
    if (note) {
      ingredients.push({ id: uid(), component: section, type: "note", name: note[1].trim(), prep: "", quantity: "", unit: "", category: "other" });
      continue;
    }
    const header = !STARTS_WITH_AMOUNT.test(line) && SECTION_LINE.exec(line);
    if (header) {
      section = capitalize(header[1].trim());
      continue;
    }
    for (const part of splitAndList(line)) {
      const { ingredient, unclear: isUnclear } = ingredientFromLine(part, section);
      if (!ingredient.name) continue;
      ingredients.push(ingredient);
      if (isUnclear) unclear.push(ingredient.id);
    }
  }
  return { ingredients, unclear };
}

// Pasted steps: one per paragraph when there are blank lines between them,
// else one per line. Numbering and bullets are dropped; section lines and
// "Note:" lines are kept as such.
export function stepsFromText(text: string, component = ""): EntryStep[] {
  const blocks = /\n\s*\n/.test(text) ? text.split(/\n\s*\n/) : text.split(/\r?\n/);
  const steps: EntryStep[] = [];
  let section = component;
  for (const block of blocks) {
    const line = block.replace(/\s*\r?\n\s*/g, " ").replace(LEADER, "").trim();
    if (!line) continue;
    const note = NOTE_LINE.exec(line);
    if (note) {
      steps.push({ id: uid(), component: section, type: "note", text: note[1].trim() });
      continue;
    }
    const header = line.length <= 61 && SECTION_LINE.exec(line);
    if (header) {
      section = capitalize(header[1].trim());
      continue;
    }
    steps.push({ id: uid(), component: section, type: "step", text: line });
  }
  return steps;
}

// A source that is only a web address belongs in the source link (0056),
// leaving the source's name empty; the recipe page then shows the site.
export function splitSourceLink<T extends Pick<Recipe, "source" | "sourceUrl">>(recipe: T): T {
  const source = (recipe.source || "").trim();
  if (!recipe.sourceUrl && /^https?:\/\/\S+$/i.test(source)) return { ...recipe, source: "", sourceUrl: source };
  return recipe;
}

// What a page left out, noted before the import fills in defaults (the
// importer sends nothing for a missing time or serving count).
export function importGaps(raw: Record<string, unknown>): { servings: boolean; times: boolean } {
  const missing = (v: unknown) => v === undefined || v === null || v === "" || Number.isNaN(Number(v));
  return { servings: missing(raw.servings), times: missing(raw.activeTime) && missing(raw.cookTime) };
}

export interface Flag {
  section: "basics" | "ingredients" | "steps";
  text: string;
}

// What the review screen asks to be looked at. Saving is allowed with
// flags outstanding, except a missing name.
export function reviewFlags(recipe: Pick<Recipe, "title" | "servings" | "activeTime" | "cookTime" | "ingredients" | "instructions">, unclear: string[] = [], gaps: { servings: boolean; times: boolean } = { servings: false, times: false }): Flag[] {
  const flags: Flag[] = [];
  if (!(recipe.title || "").trim()) flags.push({ section: "basics", text: "The recipe needs a name." });
  if (gaps.servings) flags.push({ section: "basics", text: `How many it serves wasn't given; it's set to ${recipe.servings || 4}.` });
  if (gaps.times && !recipe.activeTime && !recipe.cookTime) flags.push({ section: "basics", text: "Active and cook times are missing." });
  const ingredients = (recipe.ingredients || []) as EntryIngredient[];
  const realIngredients = ingredients.filter((i) => i.type !== "note" && (i.name || "").trim());
  if (realIngredients.length === 0) flags.push({ section: "ingredients", text: "There are no ingredients yet." });
  const unclearLeft = realIngredients.filter((i) => unclear.includes(i.id)).length;
  if (unclearLeft > 0) flags.push({ section: "ingredients", text: `${unclearLeft} ingredient amount${unclearLeft === 1 ? "" : "s"} couldn't be read.` });
  const steps = ((recipe.instructions || []) as EntryStep[]).filter((s) => s.type !== "note" && (s.text || "").trim());
  if (steps.length === 0) flags.push({ section: "steps", text: "There are no steps yet." });
  return flags;
}

// The recipe as it will be saved: empty rows dropped, a source that is a
// web address moved to the link, a missing serving count set to 4.
export function readyToSave<T extends Recipe>(recipe: T): T {
  const ingredients = ((recipe.ingredients || []) as EntryIngredient[]).filter((i) => (i.name || "").trim());
  const instructions = ((recipe.instructions || []) as EntryStep[]).filter((s) => (s.text || "").trim());
  return splitSourceLink({ ...recipe, title: recipe.title.trim(), servings: recipe.servings > 0 ? recipe.servings : 4, ingredients, instructions });
}

// Moves a row one place up (-1) or down (+1). Next to a different section,
// the row joins that section instead of swapping, so it crosses the
// section's heading one step at a time.
export function moveRow<T extends { id: string; component: string }>(rows: T[], id: string, dir: -1 | 1): T[] {
  const i = rows.findIndex((r) => r.id === id);
  const j = i + dir;
  if (i === -1 || j < 0 || j >= rows.length) return rows;
  if (rows[j].component !== rows[i].component) return rows.map((r) => (r.id === id ? { ...r, component: rows[j].component } : r));
  const out = [...rows];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}
